import { useState } from 'react';

/**
 * A sheet's typed-in values, seeded from whatever the server already holds.
 *
 * The draft cannot be seeded in an effect — that is a cascading render, and the
 * lint rule rightly refuses it. Instead the loaded sheet is remembered
 * alongside the draft and compared during render: when a new sheet arrives the
 * draft is rebuilt from it in the same pass, which is the pattern React
 * documents for state that has to follow a prop.
 */
export function useSheetDraft<TSource, TDraft>(
  source: TSource | undefined,
  build: (source: TSource) => TDraft,
  empty: TDraft,
) {
  const [state, setState] = useState<{ source: TSource | undefined; draft: TDraft }>({
    source: undefined,
    draft: empty,
  });

  if (state.source !== source) {
    setState({ source, draft: source === undefined ? empty : build(source) });
  }

  const draft = state.source === source ? state.draft : empty;

  const setDraft = (next: TDraft | ((previous: TDraft) => TDraft)) =>
    setState((previous) => ({
      source: previous.source,
      draft:
        typeof next === 'function'
          ? (next as (previousDraft: TDraft) => TDraft)(previous.draft)
          : next,
    }));

  return [draft, setDraft] as const;
}
